import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import * as doorsApi from "../../api/doors.api.js";
import { DataTable } from "../../components/DataTable.js";
import { RefreshIcon } from "../../components/Icon.js";
import { Pager } from "../../components/Pager.js";
import {
  FilterBar,
  FilterDate,
  FilterSelect,
  FilterText,
  dateParam,
} from "../../components/FilterBar.js";
import { useAuth } from "../../lib/auth-context.js";
import { formatTimestamp } from "../../lib/format.js";
import type { AuditFilters, Door } from "../../types/api.js";

const RESULTS = [
  { value: "success", label: "success" },
  { value: "failure", label: "failure" },
  { value: "blocked", label: "blocked" },
] as const;

const KINDS = [
  { value: "human", label: "a person" },
  { value: "machine", label: "an API key" },
  { value: "system", label: "the watchdog" },
] as const;

/** The actions worth offering as a menu. Anything else is reachable by typing it, and the API
 * matches on the exact recorded action string. */
const ACTIONS = [
  { value: "door.open", label: "door.open (momentary)" },
  { value: "door.hold_unlock", label: "door.hold_unlock (persistent)" },
  { value: "door.hold_lock", label: "door.hold_lock (persistent)" },
  { value: "door.release_override", label: "door.release_override" },
  { value: "door.status_check", label: "door.status_check" },
  { value: "door.watchdog_check", label: "door.watchdog_check" },
  { value: "door.register", label: "door.register" },
  { value: "door.update", label: "door.update" },
  { value: "door.remove", label: "door.remove" },
  { value: "auth.login", label: "auth.login" },
  { value: "auth.refresh_reuse_detected", label: "auth.refresh_reuse_detected" },
  { value: "user.create", label: "user.create" },
  { value: "api_key.create", label: "api_key.create" },
  { value: "api_key.revoke", label: "api_key.revoke" },
] as const;

interface FilterState {
  doorId: string;
  action: string;
  result: string;
  principalKind: string;
  principalId: string;
  from: string;
  to: string;
}

const EMPTY: FilterState = {
  doorId: "",
  action: "",
  result: "",
  principalKind: "",
  principalId: "",
  from: "",
  to: "",
};

export function AuditPage() {
  const { can } = useAuth();

  const [filters, setFilters] = useState<FilterState>(EMPTY);
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);

  // Every filter is sent to the SERVER. Narrowing rows the client already holds would answer
  // "is it on this page", and the question this screen exists for is "did it ever happen".
  const query: AuditFilters = {
    ...(filters.doorId ? { doorId: filters.doorId } : {}),
    ...(filters.action ? { action: filters.action } : {}),
    ...(filters.result ? { result: filters.result as AuditFilters["result"] } : {}),
    ...(filters.principalKind
      ? { principalKind: filters.principalKind as AuditFilters["principalKind"] }
      : {}),
    ...(filters.principalId ? { principalId: filters.principalId } : {}),
    ...(dateParam(filters.from) ? { from: dateParam(filters.from) } : {}),
    ...(dateParam(filters.to, true) ? { to: dateParam(filters.to, true) } : {}),
  };

  const audit = useQuery({
    queryKey: ["audit", query, limit, offset],
    queryFn: () => doorsApi.auditLog({ ...query, limit, offset }),
    // Keeps the previous page on screen while the next one loads, so paging does not flash an
    // empty table at a person reading a log.
    placeholderData: keepPreviousData,
  });

  // The door list drives the door filter, so an investigator picks a door rather than
  // remembering its slug.
  const doors = useQuery({ queryKey: ["doors"], queryFn: doorsApi.listDoors });

  // Verifying walks the whole chain, so it is on demand rather than on a poll. It is bounded
  // server-side now (chunked), but it is still the most expensive thing this console can ask
  // for and it has no business running on a timer.
  const verification = useQuery({
    queryKey: ["audit-verify"],
    queryFn: doorsApi.verifyAudit,
    enabled: false,
  });

  const entries = audit.data?.items ?? [];
  const page = audit.data?.page;
  const activeFilters = Object.values(filters).filter(Boolean).length;

  function update<K extends keyof FilterState>(key: K, value: FilterState[K]) {
    setFilters((current) => ({ ...current, [key]: value }));
    // Any change to what is being asked for starts again at the newest matching row - staying
    // on page 7 of a different query would show rows that have nothing to do with either.
    setOffset(0);
  }

  return (
    <>
      <div className="page-head">
        <h1 id="page-heading" tabIndex={-1}>
          Audit log
        </h1>
        <div className="row-gap">
          {can("manage_system") ? (
            <button
              type="button"
              className="btn"
              onClick={() => void verification.refetch()}
              disabled={verification.isFetching}
            >
              {verification.isFetching ? "Verifying…" : "Verify the chain"}
            </button>
          ) : null}
          <button
            type="button"
            className="btn"
            onClick={() => void audit.refetch()}
            disabled={audit.isFetching}
          >
            <RefreshIcon aria-hidden="true" focusable="false" />
            {audit.isFetching ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <p className="page-intro">
        Every control action attempted on any door — who, which door, what, and the result. Nothing
        is ever deleted from this log, and the database refuses to change a row that has been
        written. Filters below search the <strong>whole</strong> log, not just this page.
      </p>

      {verification.data ? (
        <div className={verification.data.valid ? "banner banner-ok" : "banner banner-critical"}>
          <span className="banner-icon" aria-hidden="true">
            {verification.data.valid ? "✓" : "✗"}
          </span>
          <div className="banner-body">
            {verification.data.valid ? (
              <>
                <strong>The hash chain is intact.</strong> {verification.data.detail}.
              </>
            ) : (
              <>
                <strong>THE HASH CHAIN IS BROKEN.</strong> {verification.data.detail}. Rows have
                been removed, reordered or rewritten. Treat this as a security incident: preserve
                the database and escalate.
              </>
            )}
          </div>
        </div>
      ) : null}

      <DataTable
        caption="Audit log entries, newest first"
        isLoading={audit.isLoading}
        isError={audit.isError}
        isEmpty={entries.length === 0}
        loadingMessage="Loading the audit log…"
        errorMessage="The audit log could not be loaded. Nothing has been lost — this is a display problem, not a missing record."
        emptyMessage={
          activeFilters > 0
            ? "No entries match these filters. The log still holds everything — widen or clear them."
            : "Nothing has been recorded yet."
        }
        toolbar={
          <FilterBar
            activeCount={activeFilters}
            onClear={() => {
              setFilters(EMPTY);
              setOffset(0);
            }}
          >
            <FilterSelect
              label="Door"
              value={filters.doorId}
              anyLabel="Any door"
              options={(doors.data ?? []).map((door: Door) => ({
                value: door.doorId,
                label: door.name,
              }))}
              onChange={(value) => update("doorId", value)}
            />
            <FilterSelect
              label="Action"
              value={filters.action}
              anyLabel="Any action"
              options={ACTIONS.map((a) => ({ value: a.value as string, label: a.label }))}
              onChange={(value) => update("action", value)}
            />
            <FilterSelect
              label="Result"
              value={filters.result}
              anyLabel="Any result"
              options={RESULTS.map((r) => ({ value: r.value as string, label: r.label }))}
              onChange={(value) => update("result", value)}
            />
            <FilterSelect
              label="Actor"
              value={filters.principalKind}
              anyLabel="Anyone"
              options={KINDS.map((k) => ({ value: k.value as string, label: k.label }))}
              onChange={(value) => update("principalKind", value)}
            />
            <FilterText
              label="Actor id"
              value={filters.principalId}
              placeholder="user id or system:watchdog"
              onChange={(value) => update("principalId", value)}
            />
            <FilterDate label="From" value={filters.from} onChange={(v) => update("from", v)} />
            <FilterDate label="To" value={filters.to} endOfDay onChange={(v) => update("to", v)} />
          </FilterBar>
        }
        columns={
          <>
            <th scope="col">Time</th>
            <th scope="col">Actor</th>
            <th scope="col">Action</th>
            <th scope="col">Resource</th>
            <th scope="col">Result</th>
            <th scope="col">Correlation</th>
          </>
        }
        footer={
          page ? (
            <Pager
              total={page.total}
              limit={page.limit}
              offset={page.offset}
              hasMore={page.hasMore}
              count={entries.length}
              busy={audit.isFetching}
              onOffsetChange={setOffset}
              onLimitChange={setLimit}
            />
          ) : null
        }
      >
        {entries.map((entry) => (
          <tr key={entry.id}>
            <td className="cell-sub">{formatTimestamp(entry.occurredAt)}</td>
            <td>
              <div>{entry.principalName}</div>
              <div className="cell-sub">{entry.principalKind}</div>
            </td>
            <td className="mono">{entry.action}</td>
            <td className="mono">{entry.resourceId ?? entry.resourceType}</td>
            <td>
              <span
                className={
                  entry.result === "success"
                    ? "status tone-ok"
                    : entry.result === "blocked"
                      ? "status tone-warning"
                      : "status tone-critical"
                }
              >
                <span className="status-icon" aria-hidden="true">
                  {entry.result === "success" ? "✓" : entry.result === "blocked" ? "·" : "✗"}
                </span>
                <span>{entry.result}</span>
              </span>
            </td>
            <td className="cell-sub mono">{entry.correlationId}</td>
          </tr>
        ))}
      </DataTable>
    </>
  );
}
